/**
 * Google Maps JS bootstrap loader — a readable reproduction of Google's
 * own documented inline snippet, which installs a real `google.maps.
 * importLibrary(name)` that:
 *   1. Accumulates every requested library name.
 *   2. Loads the actual Maps JS bundle exactly once, requesting all of
 *      them together.
 *   3. Once that bundle loads, Google's own code overwrites
 *      `importLibrary` with its real implementation — our stub just
 *      re-invokes whatever `importLibrary` is *at that point*, so the
 *      real one takes over transparently.
 *
 * Without this, a plain `<script src=".../js?...&libraries=places">` tag
 * races: `onload` can fire before Google's internal async fetch for that
 * library has actually populated `google.maps.places`, so reading it
 * right after `onload` intermittently returns undefined.
 */
function installBootstrapLoader(apiKey) {
    if (window.google?.maps?.importLibrary) return;

    const google = (window.google = window.google || {});
    const maps = (google.maps = google.maps || {});
    const requestedLibraries = new Set();
    let scriptPromise = null;

    const loadScript = () => {
        if (scriptPromise) return scriptPromise;
        scriptPromise = new Promise((resolve, reject) => {
            const callbackName = "__perxGoogleMapsInit";
            const params = new URLSearchParams({
                key: apiKey,
                v: "weekly",
                libraries: [...requestedLibraries].join(","),
                callback: callbackName,
                loading: "async",
            });
            window[callbackName] = resolve;
            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
            script.async = true;
            script.onerror = () => {
                scriptPromise = null;
                reject(new Error("Google Maps script failed to load"));
            };
            document.head.appendChild(script);
        });
        return scriptPromise;
    };

    maps.importLibrary = (name, ...rest) => {
        requestedLibraries.add(name);
        // Re-read maps.importLibrary inside the .then() (not a stale
        // reference) so once Google's real script loads and replaces it,
        // this naturally hands off to the real implementation.
        return loadScript().then(() => maps.importLibrary(name, ...rest));
    };
}

/**
 * Ensures the given Google Maps library (default "places") is loaded,
 * loading the base script exactly once no matter how many callers ask
 * concurrently. Resolves with `window.google` once that library's
 * namespace (e.g. `google.maps.places`) is actually populated.
 */
export default function loadGoogleMaps(library = "places") {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
        return Promise.reject(new Error("REACT_APP_GOOGLE_MAPS_API_KEY is not set"));
    }
    installBootstrapLoader(apiKey);
    return window.google.maps.importLibrary(library).then(() => window.google);
}
